import { useState } from "react";
import { TagSimple } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { SecondaryLabel } from "shared/types/file";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";

interface BulkSecondaryLabelPopoverProps {
  selectedImages: any[];
  onDone: (updatedImages: any[]) => void;
}

const BulkSecondaryLabelPopover = ({ selectedImages, onDone }: BulkSecondaryLabelPopoverProps) => {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const allLabels = Object.values(SecondaryLabel);

  // Check if a label is present on ALL selected images
  const isLabelOnAll = (label: string) =>
    selectedImages.every((img) => img.secondaryLabels?.includes(label));

  // Check if a label is present on SOME selected images
  const isLabelOnSome = (label: string) =>
    selectedImages.some((img) => img.secondaryLabels?.includes(label)) && !isLabelOnAll(label);

  const handleToggleLabel = async (label: string) => {
    setIsLoading(true);
    const shouldAdd = !isLabelOnAll(label);

    try {
      const endpoint = shouldAdd ? "add-secondary-tag" : "remove-secondary-tag";

      // Only act on images that need the change
      const imagesToUpdate = shouldAdd
        ? selectedImages.filter((img) => !img.secondaryLabels?.includes(label))
        : selectedImages.filter((img) => img.secondaryLabels?.includes(label));

      await Promise.all(
        imagesToUpdate.map((img) =>
          fetchWithAuth(
            `${API_URL}/file/${endpoint}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ fileLogId: img.id, tag: label }),
            },
            session,
          ),
        ),
      );

      // Update local state for all selected images
      const updatedImages = selectedImages.map((img) => {
        const currentLabels = img.secondaryLabels || [];
        return {
          ...img,
          secondaryLabels: shouldAdd
            ? [...new Set([...currentLabels, label])]
            : currentLabels.filter((l: string) => l !== label),
        };
      });

      onDone(updatedImages);
      toast.success(
        shouldAdd
          ? `"${label}" toegevoegd aan ${imagesToUpdate.length} foto('s)`
          : `"${label}" verwijderd van ${imagesToUpdate.length} foto('s)`,
      );
    } catch (error) {
      console.error("Error bulk updating secondary labels:", error);
      toast.error("Fout bij bijwerken labels");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 rounded-lg bg-moss-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-moss-primary/90 transition-colors">
          <TagSimple size={16} weight="bold" />
          Secundair label
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 z-[60]"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <header className="flex items-center gap-1.5 font-medium mb-3">
          <TagSimple weight="duotone" size={18} />
          <p className="text-sm">Secundair label wijzigen</p>
        </header>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Spinner className="h-5 w-5" />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {allLabels.sort().map((label) => {
              const onAll = isLabelOnAll(label);
              const onSome = isLabelOnSome(label);
              return (
                <button
                  key={label}
                  onClick={() => handleToggleLabel(label)}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    onAll
                      ? "border-moss-primary bg-moss-primary text-white"
                      : onSome
                        ? "border-moss-primary bg-moss-primary/20 text-moss-primary"
                        : "border-moss-primary bg-white text-moss-primary hover:bg-moss-primary/10"
                  }`}
                >
                  {label}
                  {onSome && " (deels)"}
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default BulkSecondaryLabelPopover;
